import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { StepContent } from "@/i18n/types";
import { useI18n } from "@/i18n";
import { TipCallout } from "./TipCallout";
import { PhotoToggle } from "./PhotoToggle";
import { RichText } from "./RichText";

const STEP_ICONS = [
  // 1 - Booking
  <svg key="book" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>,
  // 2 - Dressing
  <svg key="dress" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>,
  // 3 - Makeup
  <svg key="makeup" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
  </svg>,
  // 4 - Photography
  <svg key="photo" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
  </svg>,
  // 5 - Return
  <svg key="return" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>,
  // 6 - Receive
  <svg key="receive" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H3.75A2.25 2.25 0 001.5 6.75v12a2.25 2.25 0 002.25 2.25z" />
  </svg>,
];

interface TimelineStepProps {
  step: StepContent;
  index: number;
  total: number;
  isPhotoStep?: boolean;
}

export function TimelineStep({ step, index, total, isPhotoStep }: TimelineStepProps) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useI18n();
  const isLast = index === total - 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="relative flex gap-4 sm:gap-6"
    >
      {/* Timeline line + node */}
      <div className="flex flex-col items-center flex-shrink-0">
        {/* Node */}
        <motion.div
          whileInView={{ scale: [0.5, 1.1, 1] }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`relative z-10 w-11 h-11 rounded-2xl flex items-center justify-center ${
            step.badge
              ? "bg-surface-1 text-ink-2 border-2 border-dashed border-surface-3"
              : "bg-kra text-white shadow-soft"
          }`}
        >
          {STEP_ICONS[index]}
        </motion.div>
        {/* Line */}
        {!isLast && (
          <div className="w-[1.5px] flex-1 min-h-[24px] bg-gradient-to-b from-surface-3 to-surface-2" />
        )}
      </div>

      {/* Content card */}
      <div className="flex-1 pb-8">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full text-left group"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-3">
                  {index + 1} {t.common.stepOf} {total}
                </span>
                {step.badge && (
                  <span className="px-2 py-0.5 rounded-full bg-surface-2 text-[10px] font-medium text-ink-2 uppercase tracking-wide">
                    {t.common.optional}
                  </span>
                )}
                {step.duration && (
                  <span className="px-2 py-0.5 rounded-full bg-kra-soft text-[10px] font-medium text-kra-dark">
                    {step.duration}
                  </span>
                )}
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-ink-0 mt-1.5 group-hover:text-kra-dark transition-colors duration-200">
                {step.title}
              </h3>
            </div>

            {/* Expand icon */}
            <motion.div
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="mt-2 w-8 h-8 rounded-full bg-surface-1 flex items-center justify-center group-hover:bg-surface-2 transition-colors"
            >
              <svg className="w-4 h-4 text-ink-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          </div>

          {/* Summary always visible */}
          <p className="text-sm text-ink-2 leading-relaxed mt-2 pr-10">
            {step.summary}
          </p>
        </button>

        {/* Expandable details */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="overflow-hidden"
            >
              <div className="pt-4 space-y-3">
                {/* Detail bullets */}
                {step.details.length > 0 && (
                  <ul className="space-y-3">
                    {step.details.map((detail, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05, duration: 0.3 }}
                        className="flex gap-3 text-sm text-ink-1 leading-relaxed"
                      >
                        <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-kra-light" />
                        <span><RichText text={detail} /></span>
                      </motion.li>
                    ))}
                  </ul>
                )}

                {/* Photo delivery toggle */}
                {isPhotoStep && <PhotoToggle />}

                {/* Tips */}
                {step.tips && step.tips.length > 0 && (
                  <div className="space-y-2 pt-2">
                    {step.tips.map((tip, i) => (
                      <TipCallout key={i} text={tip} />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
